/**
 * What a session is doing right now, in the one form the interface colours by.
 *
 * Three questions get conflated if this is left to each screen: is the work
 * running, is the agent actually doing anything, and is anything wrong. They are
 * derived together here so that the dot in the session list, the frame around
 * the pane and the dot in its header cannot disagree — a session cannot be amber
 * in one place and green in another, because there is only one answer.
 */

import type { SessionKind, SessionState } from '@/types/domain'

export type SessionActivity =
  /** The agent is producing output: it is working. */
  | 'WORKING'
  /** The agent has stopped and is waiting for the operator — a permission
   *  prompt, an approval. Silent, like an idle session, and the opposite of one:
   *  somebody is being asked for something. */
  | 'WAITING'
  /** The agent said its turn was over. Distinct from idle on purpose, and the
   *  distinction is the point: an agent that has just left an answer on the
   *  screen is the one worth looking at, and until this existed it wore the same
   *  amber as one nobody had touched since yesterday. */
  | 'DONE'
  /** Alive and healthy, but nothing has happened for a while. Narrower than it
   *  used to be: the normal case — an agent between turns — is DONE now, so what
   *  is left here is "quiet, and nothing said why". That is still worth amber. */
  | 'IDLE'
  /** The session is over. Distinct from idle on purpose: idle is a live session
   *  that may speak again, and this one will not. They were the same answer
   *  until 2026-08-16, which put a finished session under "idle" in the filter
   *  and gave a normal ending the same amber as a stalled agent. */
  | 'ENDED'
  /** The session failed, or something about it is broken. */
  | 'PROBLEM'
  /** Nobody has measured this. Not idle, not broken — unmeasured. */
  | 'UNKNOWN'

/**
 * How stale the last run of work may be before a session reads as idle.
 *
 * The server decides what counts as work at all — output must keep arriving for
 * a second, so that a screen repaint is not mistaken for an agent (minBusyRun in
 * internal/terminal/session.go). This is the second half of the same judgement:
 * how long after that run the agent is still called working.
 *
 * It has to clear the *payload's own age*, not just the gap between two frames
 * of a spinner. The session list is refetched every five seconds and `staleMs`
 * is added to the server's measurement, so a window of six seconds left about a
 * second of headroom: any session whose output came in bursts crossed the line
 * between one poll and the next and crossed back when the next payload landed —
 * the five-second flicker between "working" and "idle" an operator reported.
 * Twelve clears the poll interval and leaves seven seconds of genuine silence
 * before a session is called idle.
 *
 * This governs only the fallback now. Both provider CLIs say whether they are
 * working in their terminal title, and that answer is a level with no age to
 * cross — see `working` below.
 */
const WORKING_WINDOW_MS = 12_000

/** Session states that are their own answer, whatever the output stream says.
 *  FAILED is deliberately not here: it is answered earlier and more loudly, and
 *  "this one broke" outranks "this one is over". */
const TERMINAL_STATES: ReadonlySet<SessionState> = new Set([
  'COMPLETED',
  'TERMINATED',
  'CHECKPOINTED',
])

export interface ActivityInput {
  state?: SessionState
  /** OBSERVED means Kimene can see the session exists but holds no PTY into it,
   *  so there is no output stream to judge activity from. */
  kind?: SessionKind
  /** How long ago the server last saw this session working, measured on the
   *  server, as of the moment it produced this payload. */
  busyAgoMs?: number | null
  /** How long ago the agent said it had stopped for a human. Absent means it has
   *  not said so — or was launched without the hook that reports it, which is a
   *  different thing and is why this never proves the negative. */
  waitingAgoMs?: number | null
  /** The agent saying, in its own terminal title, that it is at work right now.
   *
   *  Absent is not "idle": a pane running a shell or a build has no such signal
   *  to give, and those are judged by output as before. It exists because output
   *  cannot answer the question for an agent waiting on a tool — measured on
   *  this fleet, a session ran 45 seconds without printing while genuinely
   *  working, and the light called it idle for all of them. */
  working?: boolean
  /** How long ago this browser received that payload.
   *
   *  The two are added rather than one timestamp being subtracted from the
   *  browser's clock. Each term is a duration measured on one machine, so the
   *  answer no longer depends on a laptop and a server agreeing about the time —
   *  a few seconds of disagreement would otherwise shift a six-second window
   *  wholesale, and an unsynced clock would pin the light on one colour. */
  staleMs?: number
  /** How long ago the agent said its turn was finished — Claude's Stop hook,
   *  Codex's agent-turn-complete. Absent means no turn has reported itself the
   *  latest thing to have happened, which is *not* "the turn is unfinished":
   *  nothing here derives a negative from it. See `reportsTurns`. */
  doneAgoMs?: number | null
  /** Whether this session could report the above at all. Not used to decide the
   *  activity — a quiet unhooked session still reads IDLE, exactly as it did
   *  before this existed — but carried so the interface can say *why* it is
   *  quiet. Without it an adopted session reads amber beside an identical hooked
   *  one and nothing explains the difference, and amber reads as "stalled". */
  reportsTurns?: boolean
  /** Whether a PTY client stands between the session and its tmux. False means
   *  the work is running and nothing is reading it. Undefined means this process
   *  has no view of the session at all, which is not the same claim. */
  attached?: boolean
  /** The pane's socket, where there is one. A pane that cannot reach the server
   *  knows nothing about the session and must not imply that it does. */
  connected?: boolean
}

export function sessionActivity(input: ActivityInput): SessionActivity {
  const { state, kind, busyAgoMs, waitingAgoMs, doneAgoMs, staleMs = 0, attached, connected, working } =
    input

  // A failure outranks everything: an agent that errored is not "idle", and its
  // last burst of output was probably the error itself.
  if (state === 'FAILED') return 'PROBLEM'

  // An observed session is one Kimene can see but never opened: no PTY, so no
  // output stream, so nothing to judge. Unknown — emphatically not a problem.
  // Reading its (necessarily false) attached flag as a fault would paint every
  // observed session red for doing exactly what observed means.
  if (kind === 'OBSERVED') return 'UNKNOWN'

  // The work is running and nothing is reading it. The node retries, but until
  // it succeeds no output arrives and no keystroke lands, so this is a problem
  // with the session rather than a quiet agent.
  //
  // Only while the work *is* running, and that qualifier is load-bearing: the
  // premise of the sentence above is false for a session that has finished. A
  // COMPLETED session whose pane tmux is still holding reports `attached: false`
  // for the ordinary reason that there is nothing left to read, and this rule
  // painted that red — a louder version of the exact mistake the ENDED band was
  // added to stop, which was painting a normal ending amber. FAILED is answered
  // above and still outranks everything, so a broken session is unaffected.
  if (attached === false && !(state && TERMINAL_STATES.has(state))) return 'PROBLEM'

  // The pane lost its socket. It cannot see the session, so it says so rather
  // than reporting whatever it last knew as though it were current.
  if (connected === false) return 'PROBLEM'

  // Being asked for something outranks how long the session has been quiet: the
  // agent went silent *because* it is waiting, so by output alone this is the
  // case that looks most like "finished". It is ranked above the timing rules
  // and below the failures, because a session that has broken is not waiting for
  // an answer it could still use.
  //
  // Only a mark newer than the last work counts, and the server has already
  // applied that test — answering the prompt makes the agent work again, which
  // supersedes the mark, so nothing has to clear it.
  if (waitingAgoMs != null && !Number.isNaN(waitingAgoMs)) return 'WAITING'

  // A session that has finished is not idle in the sense this light means, and
  // colouring it amber puts a warning on a normal ending. It said IDLE anyway
  // until this was fixed, which is how "boşta" came to list yesterday's finished
  // work beside a live session nobody is typing into.
  if (state && TERMINAL_STATES.has(state)) return 'ENDED'

  // The stored state saying, in as many words, that a person is being waited on.
  //
  // It answered IDLE until 2026-08-24 — under a comment that said "waiting on a
  // human is the clearest form of not working", which is true and is not what
  // IDLE means here. The branch predates `WAITING` existing as an activity at
  // all, and adding that state left this one behind: the one input that states
  // the answer outright was mapped to its opposite, and would have painted a
  // blocked agent the resting colour of the fleet.
  //
  // Nothing in the control plane writes this state today, which is exactly why
  // it survived — a branch no fixture reaches is a branch no test can fail. It
  // is corrected rather than deleted because the constant is real, the API
  // accepts it, and the next thing to set it must not inherit the old answer.
  if (state === 'INPUT_REQUIRED') return 'WAITING'

  // The agent's own answer, and it outranks the output rule below because it is
  // better evidence: bytes are a proxy for work, this is the CLI saying so. It
  // sits *under* the waiting checks on purpose — a session stopped at a prompt
  // is not working however its spinner is behaving.
  if (working) return 'WORKING'

  // Below `working` on purpose, and this is the load-bearing boundary: `working`
  // is a *level* read from the agent's title right now, while this is an *edge*
  // stamped in the past. What is true now beats what was true then — which is
  // what protects the measured case of an agent that ran 45 seconds without
  // printing while genuinely working.
  //
  // Above the output fallback below, which is the line a finished turn falls
  // through today and lands on IDLE. Like the waiting rule it carries no window
  // and no age comparison, so there is no boundary for `staleMs` to walk a
  // payload across — the shape that produced the five-second flicker is absent
  // rather than tuned.
  if (doneAgoMs != null && !Number.isNaN(doneAgoMs)) return 'DONE'

  if (busyAgoMs == null || Number.isNaN(busyAgoMs)) {
    // Never seen working. That is unknown while the session is still starting —
    // there has been no chance to observe it — and idle once it is established.
    return state === 'RUNNING' || state === 'IDLE' ? 'IDLE' : 'UNKNOWN'
  }

  return busyAgoMs + Math.max(0, staleMs) <= WORKING_WINDOW_MS ? 'WORKING' : 'IDLE'
}

/** Names the activity light, so its colour is never the only way to read it —
 *  the same reason every other state in this interface carries a word.
 *
 *  It lives beside the derivation rather than on one screen for the reason the
 *  module exists: the session list and the terminal header read the same light,
 *  so they must also say the same word about it. */
export function activityLabelKey(activity: SessionActivity) {
  switch (activity) {
    case 'WORKING':
      return 'session.activity.working'
    case 'WAITING':
      return 'session.activity.waiting'
    case 'DONE':
      return 'session.activity.done'
    case 'IDLE':
      return 'session.activity.idle'
    case 'ENDED':
      return 'session.activity.ended'
    case 'PROBLEM':
      return 'session.activity.problem'
    case 'UNKNOWN':
      return 'session.activity.unknown'
  }
}

/** Whether a session has finished, whatever its outcome was — the client-side
 *  reading of domain.SessionState.Ended(). It decides which of two different
 *  acts a row offers: ending a session that is still going, or clearing away
 *  what one that already ended left behind. */
export function sessionHasEnded(state: SessionState): boolean {
  return state === 'COMPLETED' || state === 'FAILED' || state === 'TERMINATED'
}
